import { Icon } from '@/components/icon/icon';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAppearance } from '@/hooks/use-appearance';
import { useLocale } from '@/hooks/use-locale';
import { THEME_OPTIONS } from '@/utils/appearance-options';
import { LOCALE_DISPLAY_NAMES, LOCALE_FLAGS } from '@/utils/locale-url-helper';
import { Languages, Monitor } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SettingsToggleProps {
    showText?: boolean;
    variant?: 'combined' | 'split';
}

export default function SettingsToggle({ showText = false, variant = 'combined' }: SettingsToggleProps) {
    const { t } = useTranslation();
    const { currentLocale, availableLocales, changeLocale } = useLocale();
    const { appearance, updateAppearance } = useAppearance();

    const CurrentThemeIcon = THEME_OPTIONS.find((option) => option.key === appearance)?.icon || Monitor;

    // Split variant: render two separate dropdowns side by side
    if (variant === 'split') {
        return (
            <div className="flex items-center gap-1">
                {/* Language Toggle */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Languages className="h-4 w-4" />
                            <span className="sr-only">{t('settings.language')}</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {availableLocales?.map((locale) => (
                            <DropdownMenuItem
                                key={locale}
                                onClick={() => changeLocale(locale)}
                                className={`my-0.5 flex items-center gap-2 ${currentLocale === locale ? 'bg-accent text-accent-foreground' : ''}`}
                            >
                                <span className="text-lg">{LOCALE_FLAGS[locale as keyof typeof LOCALE_FLAGS]}</span>
                                <span>{LOCALE_DISPLAY_NAMES[locale as keyof typeof LOCALE_DISPLAY_NAMES] || locale}</span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* Theme Toggle */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                            <CurrentThemeIcon className="h-4 w-4" />
                            <span className="sr-only">{t('settings.theme')}</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {THEME_OPTIONS.map(({ key, icon: IconComponent }) => (
                            <DropdownMenuItem
                                key={key}
                                onClick={() => updateAppearance(key)}
                                className={`my-0.5 flex items-center gap-2 ${appearance === key ? 'bg-accent text-accent-foreground' : ''}`}
                            >
                                <IconComponent className="h-4 w-4 text-inherit" />
                                <span>{t(`settings.appearance.${key}`)}</span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        );
    }

    // Combined variant: single dropdown with both sections
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className={showText ? 'w-full justify-start gap-2' : 'gap-2'}>
                    <Icon iconNode={Languages} className="h-4 w-4" />
                    {showText ? <span>{t('navigation.settings')}</span> : <CurrentThemeIcon className="h-4 w-4" />}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
                {/* Locale Section */}
                <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">{t('settings.language')}</div>
                {availableLocales?.map((locale) => (
                    <DropdownMenuItem
                        key={locale}
                        onClick={() => changeLocale(locale)}
                        className={`mx-1 my-0.5 flex items-center gap-2 rounded-sm px-2 py-1.5 ${currentLocale === locale ? 'bg-accent text-accent-foreground' : ''}`}
                    >
                        <span className="text-lg">{LOCALE_FLAGS[locale as keyof typeof LOCALE_FLAGS]}</span>
                        <span>{LOCALE_DISPLAY_NAMES[locale as keyof typeof LOCALE_DISPLAY_NAMES] || locale}</span>
                    </DropdownMenuItem>
                ))}

                <DropdownMenuSeparator />

                {/* Theme Section */}
                <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">{t('settings.theme')}</div>
                {THEME_OPTIONS.map(({ key, icon: IconComponent }) => (
                    <DropdownMenuItem
                        key={key}
                        onClick={() => updateAppearance(key)}
                        className={`mx-1 my-0.5 flex items-center gap-2 rounded-sm px-2 py-1.5 ${appearance === key ? 'bg-accent text-accent-foreground' : ''}`}
                    >
                        <IconComponent className="h-4 w-4 text-inherit" />
                        <span>{t(`settings.appearance.${key}`)}</span>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
